/**
 * Register Component
 * 
 * Multi-step registration form for academic conference website.
 * Handles user registration with personal information, affiliations,
 * research areas, and account creation.
 * 
 * @returns {JSX.Element} Complete registration form with step navigation
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { registerUser } from "../../services/auth";

// Import step components
import PersonalInformation from "./PersonalInformation";
import AffiliationsForm from "./AffiliationsForm";
import AreasOfExpertise from "./AreasOfExpertise";
import AccountInformation from "./AccountInformation";
import RegistrationStepper from "./RegistrationStepper";
import RegistrationInfo from "./RegistrationInfo";

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRetype, setShowPasswordRetype] = useState(false);
  const [useCustomUsername, setUseCustomUsername] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 - Personal Information
    salutation: "",
    firstName: "",
    middleName: "",
    lastName: "",
    primaryEmail: "",
    primaryEmailConfirm: "",
    secondaryEmail: "",
    secondaryEmailConfirm: "",
    workPhone: "",
    workPhoneCountry: "+1",
    cellPhone: "",
    cellPhoneCountry: "+1",
    personalWebPage: "",

    // Step 2 - Affiliations
    primaryInstitution: "",
    primaryRank: "",
    primaryRankOther: "",
    primaryDepartment: "",
    primaryAddress1: "",
    primaryAddress2: "",
    primaryCity: "",
    primaryPostalCode: "",
    primaryCountry: "",
    primaryState: "",

    secondaryInstitution: "",
    secondaryRank: "",
    secondaryRankOther: "",
    secondaryDepartment: "",
    secondaryAddress1: "",
    secondaryAddress2: "",
    secondaryCity: "",
    secondaryPostalCode: "",
    secondaryCountry: "",
    secondaryState: "",

    // Step 3 - Areas of Expertise
    primary1Category: "",
    primary1: "",
    primary2Category: "",
    primary2: "",
    primary3Category: "",
    primary3: "",

    // Step 4 - Account Information
    username: "",
    password: "",
    passwordRetype: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  /**
   * Handles form input changes
   * Updates the form data state with new values
   * 
   * @param {Event} e - Input change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
  };

  /**
   * Validates the current step's required fields
   * Each step has different validation requirements
   * 
   * @param {number} step - Step number to validate (1-4)
   * @returns {boolean} Whether the step is valid
   */
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return (
          formData.salutation &&
          formData.firstName &&
          formData.lastName &&
          formData.primaryEmail &&
          formData.primaryEmailConfirm &&
          formData.primaryEmail === formData.primaryEmailConfirm
        );
      case 2:
        return (
          formData.primaryAddress1 &&
          formData.primaryCity &&
          formData.primaryPostalCode &&
          formData.primaryCountry
        );
      case 3:
        return (
          formData.primary1Category && formData.primary1 && 
          formData.primary2Category && formData.primary2 && 
          formData.primary3Category && formData.primary3
        );
      case 4:
        return (
          (useCustomUsername ? formData.username : formData.primaryEmail) &&
          formData.password &&
          formData.passwordRetype &&
          formData.password === formData.passwordRetype &&
          formData.password.length >= 8
        );
      default:
        return true;
    }
  };

  /**
   * Advances to the next step after validation
   * Shows error message if validation fails
   */
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      setError("");
    } else {
      setError("Please fill in all required fields correctly.");
    }
  };

  /**
   * Goes back to the previous step
   * Clears any existing error messages
   */
  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    setError("");
  };

  /**
   * Handles form submission for the final step
   * Validates all data and submits registration
   * 
   * @param {Event} e - Form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(4)) {
        setError("Please fill in all required fields correctly and make sure that the two passwords match.");
        return;
    }

    setLoading(true);
    setError("");
    
    try {
        // Create submission data with correct username
        const submissionData = {
            ...formData,
            username: useCustomUsername ? formData.username : formData.primaryEmail
        };
        
        const response = await registerUser(submissionData);
        // Navigate to confirmation page
        navigate("/registration-confirmation");
    } catch (err) {
        console.error('Registration error:', err);
        
        // Handle specific error messages from server
        if (err.response && err.response.data && err.response.data.message) {
            setError(err.response.data.message);
        } else {
            setError("Registration failed. Please try again.");
        }
    } finally {
        setLoading(false);
    }
  };

  /**
   * Renders the appropriate step component based on current step
   * Each step is handled by a separate component for better organization
   * 
   * @returns {JSX.Element} Current step component
   */
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInformation 
            formData={formData} 
            handleChange={handleChange} 
          />
        );
      case 2:
        return (
          <AffiliationsForm 
            formData={formData} 
            handleChange={handleChange} 
          />
        );
      case 3:
        return (
          <AreasOfExpertise 
            formData={formData} 
            handleChange={handleChange} 
          />
        );
      case 4:
        return (
          <AccountInformation 
            formData={formData} 
            handleChange={handleChange}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            showPasswordRetype={showPasswordRetype}
            setShowPasswordRetype={setShowPasswordRetype}
            useCustomUsername={useCustomUsername}
            setUseCustomUsername={setUseCustomUsername}
          />
        );
      default:
        return (
          <PersonalInformation 
            formData={formData} 
            handleChange={handleChange} 
          />
        );
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-7">
          <div className="card card-conference shadow">
            <div className="card-header bg-conference-gradient text-white text-center">
              <h2 className="mb-0 text-white">
                <i className="bi bi-person-plus me-2"></i>
                Registration to MFC
              </h2>
              <p className="mb-0 mt-2">Step {currentStep} of 4</p>
            </div>

            {/* Progress Bar */}
            <div
              className="progress"
              style={{ height: "4px", borderRadius: 0 }}
            >
              <div
                className="progress-bar bg-conference-gradient"
                style={{ width: `${(currentStep / 4) * 100}%` }}
              ></div>
            </div>

            <div className="card-body p-4">
              {error && (
                <div
                  className="alert alert-danger alert-dismissible fade show"
                  role="alert"
                >
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError("")}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="form-conference">
                {renderCurrentStep()}

                {/* Navigation Buttons */}
                <RegistrationStepper
                  currentStep={currentStep}
                  nextStep={nextStep}
                  prevStep={prevStep}
                  handleSubmit={handleSubmit}
                  loading={loading}
                />
              </form>

              <div className="text-center mt-4">
                <p className="text-muted mb-0">
                  Already have an account?
                  <a
                    href="/login"
                    className="text-decoration-none ms-1 text-conference-primary"
                  >
                    Sign in here
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Additional Information Card */}
          <RegistrationInfo />
        </div>
      </div>
    </div>
  );
};

export default Register;
